import { isDpo } from "src/modules/data-protection/constants/organizationFunctions";

export const DATA_PROTECTION_MODULE = "gd";

export const isDataProtectionModule = (activeModule) =>
    activeModule === DATA_PROTECTION_MODULE;

export const extractFunctions = (payload) => {
    if (Array.isArray(payload?.functions)) {
        return payload.functions;
    }

    if (Array.isArray(payload?.data?.functions)) {
        return payload.data.functions;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    return [];
};

export const toUserId = (value) => {
    if (value == null || value === "") {
        return null;
    }

    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : null;
};

export const mapFunctionsToOptions = (functions = []) =>
    (functions || [])
        .filter((item) => item?.key)
        .map((item) => ({
            value: item.key,
            label: item.title || item.key,
        }));

export const getSelectedKeys = (selected) =>
    (selected || []).map((option) => option?.value).filter(Boolean);

export const getAssignedFunctionKeysForUser = (functions, userId) => {
    const normalizedId = toUserId(userId);

    if (normalizedId == null) {
        return [];
    }

    return (functions || [])
        .filter((item) =>
            (item?.assigned_users || []).some(
                (user) => toUserId(user?.id) === normalizedId
            )
        )
        .map((item) => item.key)
        .filter(Boolean);
};

export const getDpoAssignedUserId = (functions) => {
    const dpoFunction = (functions || []).find((item) => isDpo(item?.key));
    const assigned = Array.isArray(dpoFunction?.assigned_users)
        ? dpoFunction.assigned_users[0]
        : null;

    return toUserId(assigned?.id);
};

export const shouldConfirmDpoReplace = ({
    functions,
    selectedKeys = [],
    nextKeys = [],
    currentUserId,
} = {}) => {
    const addingDpo =
        nextKeys.some((key) => isDpo(key)) &&
        !selectedKeys.some((key) => isDpo(key));

    if (!addingDpo) {
        return false;
    }

    const assignedId = getDpoAssignedUserId(functions);

    if (assignedId == null) {
        return false;
    }

    const currentId = toUserId(currentUserId);

    if (currentId != null && currentId === assignedId) {
        return false;
    }

    return true;
};

export const diffFunctionAssignments = ({
    previousKeys = [],
    nextKeys = [],
} = {}) => {
    const previous = new Set(previousKeys);
    const next = new Set(nextKeys);

    return {
        toAssign: [...next].filter((key) => !previous.has(key)),
        toUnassign: [...previous].filter((key) => !next.has(key)),
    };
};

const unwrapCreatedUsers = (value) => {
    if (!value) {
        return [];
    }

    if (Array.isArray(value)) {
        return value;
    }

    if (Array.isArray(value.users)) {
        return value.users;
    }

    if (Array.isArray(value.data) && !value.data.key) {
        return value.data;
    }

    if (value.user && typeof value.user === "object") {
        return [value.user];
    }

    if (value.id != null) {
        return [value];
    }

    return [];
};

export const extractCreatedUsers = (response) => {
    if (!response) {
        return [];
    }

    const fromRoot = unwrapCreatedUsers(response);

    if (fromRoot.length) {
        return fromRoot;
    }

    return unwrapCreatedUsers(response.data);
};

export const findCreatedUserId = ({ createdUsers, email, index }) => {
    const normalizedEmail = String(email || "")
        .trim()
        .toLowerCase();

    if (normalizedEmail) {
        const match = (createdUsers || []).find(
            (user) =>
                String(user?.email || "")
                    .trim()
                    .toLowerCase() === normalizedEmail
        );

        if (match?.id != null) {
            return match.id;
        }
    }

    const byIndex = createdUsers?.[index];

    return byIndex?.id ?? null;
};

export const extractUsersFromList = (payload) => {
    if (Array.isArray(payload?.users)) {
        return payload.users;
    }

    if (Array.isArray(payload?.data?.users)) {
        return payload.data.users;
    }

    if (Array.isArray(payload?.data)) {
        return payload.data;
    }

    if (Array.isArray(payload)) {
        return payload;
    }

    return [];
};
